import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'gatsby';
import MenuIcon from '@assets/icons/MenuIcon';
import CloseIcon from '@assets/icons/CloseIcon';
import LanguageSelector from '@components/LanguageSelector';
import { mobileHeaderActionsPosition, theme } from '@styles';
import { MenuData, MenuLink, PrismicFooter } from '@appTypes';
import { BLACK_HAZE, LIMED_SPRUCE, MYSTIC, OUTER_SPACE, WHITE } from '@constants';
import ChevronBottomIcon from '@assets/icons/ChevronBottomIcon';
import ChevronTopIcon from '@assets/icons/ChevronTopIcon';
import OutsideClickHandler from 'react-outside-click-handler';
import { useWindowSize } from 'react-use';
import { NavigationThemeContext } from '../context/NavigationThemeContext';
import { useI18n } from '../context/I18nContext';
import { getLocalizedPath, getSearchWithoutCacheBustParam, teamsCtaLocation } from '@utils/websiteUrlUtils';
import type { LocaleCode } from '@config/i18n';
import { isBrowser } from '@utils';
import { useOutboundHref } from '@customHooks';

const { colors, breakpoints } = theme;

const withApiFirst = (items: MenuLink['items']): MenuLink['items'] => {
  const apiIndex = items.findIndex(
    (item) => item?.inner_link === '/content-api' || item?.inner_link_id === 'content-api'
  );
  if (apiIndex <= 0) return items;
  const next = items.slice();
  const [apiItem] = next.splice(apiIndex, 1);
  next.unshift(apiItem);
  return next;
};

type Props = {
  menuData: MenuData;
  footerData: PrismicFooter;
};

const Menu: React.FC<Props> = ({ menuData, footerData }) => {
  const { copyright_text, footer_logo } = footerData;
  const [menuOpen, setMenuOpen] = useState(false);
  const { body } = menuData;
  const menuLinks = body;
  const [openedSubmenu, setOpenedSubmenu] = useState<string | undefined>(undefined);
  const { width } = useWindowSize();
  const { navigationTheme } = useContext(NavigationThemeContext);
  const { locale, t } = useI18n();
  const currentLocale = locale as LocaleCode;

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleAnchorClick = () => {
    setMenuOpen(false);
  };

  const renderLinks = () => {

    // Defensive coding following existing patterns
    if (!menuLinks || !Array.isArray(menuLinks)) {
      return null;
    }

    return menuLinks.map((linkItem, index) => {
      // Null safety following existing patterns
      if (!linkItem?.primary) {
        return null;
      }

      const { link, link_label, link_is_external, link_is_button, link_id } = linkItem.primary;
      const uniqueKey = link_label || link || `menu-item-${index}`;
      const hasChildren = Array.isArray(linkItem.items) && linkItem.items.length > 0;
      const submenuItems = hasChildren ? withApiFirst(linkItem.items) : linkItem.items;
      const submenuOpen = openedSubmenu === link_label;

      const submenu = hasChildren ? (
        <InnerLinksWrapper $open={submenuOpen}>
          {submenuItems.map((linkInnerItem, innerIndex) => {
            if (!linkInnerItem || linkInnerItem.hidden) {
              return null;
            }

            const innerIsExternal = linkInnerItem.inner_link?.includes('http');

            if (innerIsExternal && linkInnerItem.inner_link) {
              return (
                <ExternalSubmenuLink
                  key={linkInnerItem.inner_link_label || `inner-${innerIndex}`}
                  link={linkInnerItem.inner_link}
                  linkId={linkInnerItem.inner_link_id}
                  label={linkInnerItem.inner_link_label}
                  isFirst={innerIndex === 0}
                  isLast={innerIndex === submenuItems.length - 1}
                  onNavigate={() => {
                    handleAnchorClick();
                    setOpenedSubmenu(undefined);
                  }}
                />
              );
            }

            const innerBasePath = linkInnerItem.inner_link
              ? linkInnerItem.inner_link.includes('#')
                ? `/${linkInnerItem.inner_link}`
                : linkInnerItem.inner_link
              : '/';
            const innerLocalizedPath = getLocalizedPath(
              innerBasePath.startsWith('/') ? innerBasePath : `/${innerBasePath}`,
              currentLocale
            );
            const innerSearch = isBrowser() ? getSearchWithoutCacheBustParam(window.location.search) : '';
            return (
              <InnerLink
                isFirst={innerIndex === 0}
                isLast={innerIndex === submenuItems.length - 1}
                activeStyle={{ fontWeight: '700' }}
                to={linkInnerItem.inner_link ? `${innerLocalizedPath}${innerSearch}` : innerLocalizedPath}
                onClick={() => {
                  handleAnchorClick();
                  setOpenedSubmenu(undefined);
                }}
                id={linkInnerItem.inner_link_id}
                key={linkInnerItem.inner_link_label || `inner-${innerIndex}`}
              >
                {linkInnerItem.inner_link_label}
              </InnerLink>
            );
          })}
        </InnerLinksWrapper>
      ) : null;

      if (hasChildren) {
        return (
          <OutsideClickHandler
            key={uniqueKey}
            disabled={width < 769 || !submenuOpen}
            onOutsideClick={() => setOpenedSubmenu(undefined)}
          >
            <MenuGroup>
              <ParentButton
                type="button"
                aria-expanded={submenuOpen}
                onClick={() => setOpenedSubmenu(submenuOpen ? undefined : link_label)}
              >
                <LinkLabel
                  theme={navigationTheme}
                  isButton={link_is_button}
                  id={link_id}
                >
                  <div id={link_id}>{link_label}</div>
                  <ChevronIconWrapper>
                    {submenuOpen ? (
                      <ChevronTopIcon stroke={
                        (navigationTheme === 'dark' && !link_is_button) || (navigationTheme === 'light' && link_is_button)
                          ? '#fff'
                          : OUTER_SPACE
                      } />
                    ) : (
                      <ChevronBottomIcon stroke={
                        (navigationTheme === 'dark' && !link_is_button) || (navigationTheme === 'light' && link_is_button)
                          ? '#fff'
                          : OUTER_SPACE
                      } />
                    )}
                  </ChevronIconWrapper>
                </LinkLabel>
              </ParentButton>
              {submenu}
            </MenuGroup>
          </OutsideClickHandler>
        );
      }

      const isExternalLink = link?.includes('http');

      if (isExternalLink && link) {
        return (
          <ExternalMenuLink
            key={uniqueKey}
            link={link}
            linkId={link_id}
            isButton={link_is_button}
            label={link_label}
            navigationTheme={navigationTheme}
            onNavigate={handleAnchorClick}
          />
        );
      } else {
        const basePath = link ? (link.includes('#') ? `/${link}` : link) : '/';
        const localizedPath = getLocalizedPath(basePath.startsWith('/') ? basePath : `/${basePath}`, currentLocale);
        const search = isBrowser() ? getSearchWithoutCacheBustParam(window.location.search) : '';
        return (
          <MainLink
            activeStyle={{ fontWeight: '700' }}
            rel='noreferrer'
            to={link ? `${localizedPath}${search}` : localizedPath}
            key={uniqueKey}
            target={link_is_external ? '_blank' : 'initial'}
            onClick={() => {
              handleAnchorClick();
            }}
          >
            <LinkLabel
              theme={navigationTheme}
              isButton={link_is_button}
              id={link_id}
            >
              <div id={link_id}>{link_label}</div>
            </LinkLabel>
          </MainLink>
        );
      }
    }).filter(Boolean); // Remove null items
  };

  return (
    <Container>
      <MobileHeaderActions>
        <LanguageSelector variant="header" onMenuClose={handleAnchorClick} />
        <MenuIcons opened={menuOpen}>
          {!menuOpen ? (
            <MenuIcon onClick={handleMenuClick} width={24} color='#000' />
          ) : (
            <CloseIcon onClick={handleMenuClick} width={24} color='#fff' />
          )}
        </MenuIcons>
      </MobileHeaderActions>

      <DesktopNavigation>
        <MainLinksWrapper>{renderLinks()}</MainLinksWrapper>
      </DesktopNavigation>

      {menuOpen && (
        <MobileNavigation>
          <MainLinksWrapper>{renderLinks()}</MainLinksWrapper>
          <FooterContainer>
            {footer_logo?.url && <img src={footer_logo.url} alt={footer_logo.alt || t('common:alt.footerLogo')} />}
            <FooterHeader>
              <div>{copyright_text}</div>
            </FooterHeader>
          </FooterContainer>
        </MobileNavigation>
      )}
    </Container>
  );
};

export default Menu;

const ChevronIconWrapper = styled.div``;

const MenuGroup = styled.div`
  position: relative;
`;

const ParentButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-align: inherit;
`;

const LinkLabel = styled.div<{ isButton?: boolean }>`
  display: flex;
  align-items: center;
  svg {
    margin-left: 2px;
  }
  &:hover {
    opacity: 0.75;
  }
  color: #fff;
  font-size: ${({ isButton }) => (isButton ? '16px' : '24px')};
  line-height: 36px;
  letter-spacing: -0.2px;
  margin-bottom: 24px;
  justify-content: space-between;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    color: ${({ isButton, theme }) =>
    (theme === 'dark' && !isButton) || (theme === 'light' && isButton) ? '#fff' : OUTER_SPACE};
    font-size: ${({ isButton }) => (isButton ? '14px' : '16px')};
    font-weight: ${({ isButton }) => (isButton ? '700' : 'auto')};
    border-radius: 12px;
    line-height: 18.2px;
    margin-bottom: 0px;
  }
  ${({ isButton, theme }) =>
    isButton &&
    `
    padding: 16px 24px;
    background-color: ${LIMED_SPRUCE};
    transform: translateY(0px);
    &:hover {
      cursor: pointer;
      transform: translateY(-3px);
      filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.25));
      opacity: 1;
    }
    transition: all 250ms ease-in-out;
    border-radius: 12px;
    display: inline-flex;
    line-height: 22.2px;
    font-weight: 700;
    @media screen and (min-width: ${breakpoints.smallLaptop}) {
      background-color: ${theme === 'dark' ? WHITE : OUTER_SPACE};
    }
  `}
`;

const MainLinksWrapper = styled.div`
  margin-top: max(80px, calc(env(safe-area-inset-top, 0px) + 64px));
  padding: 0 32px;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    margin-top: initial;
    padding: initial;
    gap: 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const InnerLinksWrapper = styled.div<{ $open?: boolean }>`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    margin-top: 16px;
    background: #fff;
    border-radius: 12px;
    width: fit-content;
    min-width: 200px;
    position: absolute;
    border: 1px solid ${MYSTIC};
    z-index: 1000;
  }
`;

const MainLink = styled(Link)`
  position: relative;
  text-decoration: none;
  cursor: pointer;
`;

const ExternalMainLink = styled.a`
  position: relative;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
`;

const InnerLink = styled(Link) <{ isFirst: boolean; isLast: boolean }>`
  text-decoration: none;
  font-weight: 400;
  font-size: 24px;
  line-height: 36px;
  letter-spacing: -0.2px;
  color: #fff;
  margin-bottom: 24px;
  padding: 0 16px;
  white-space: nowrap;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    color: ${OUTER_SPACE};
    margin-bottom: 0px;
    font-size: 16px;
    line-height: 27px;
    height: 53px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 24px;
    white-space: nowrap;
    border-top-left-radius: ${({ isFirst }) => (isFirst ? '12px' : '0px')};
    border-top-right-radius: ${({ isFirst }) => (isFirst ? '12px' : '0px')};
    border-bottom-left-radius: ${({ isLast }) => (isLast ? '12px' : '0px')};
    border-bottom-right-radius: ${({ isLast }) => (isLast ? '12px' : '0px')};
    &:hover {
      background: ${BLACK_HAZE};
    }
  }
`;

const ExternalInnerLink = styled.a<{ isFirst: boolean; isLast: boolean }>`
  text-decoration: none;
  font-weight: 400;
  font-size: 24px;
  line-height: 36px;
  letter-spacing: -0.2px;
  color: #fff;
  margin-bottom: 24px;
  padding: 0 16px;
  cursor: pointer;
  white-space: nowrap;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    color: ${OUTER_SPACE};
    margin-bottom: 0px;
    font-size: 16px;
    line-height: 27px;
    height: 53px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 24px;
    white-space: nowrap;
    border-top-left-radius: ${({ isFirst }) => (isFirst ? '12px' : '0px')};
    border-top-right-radius: ${({ isFirst }) => (isFirst ? '12px' : '0px')};
    border-bottom-left-radius: ${({ isLast }) => (isLast ? '12px' : '0px')};
    border-bottom-right-radius: ${({ isLast }) => (isLast ? '12px' : '0px')};
    &:hover {
      background: ${BLACK_HAZE};
    }
  }
`;

const FooterContainer = styled.div`
  padding: 50px 24px 0px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  img {
    width: 118px;
  }
  @media screen and (min-width: ${breakpoints.phone}) {
    padding: 80px 32px 0px 32px;
  }
`;

const FooterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${colors.nevada};
  font-size: 14px;
  line-height: 18.2px;
  font-weight: 400;
`;

const Container = styled.div`
  height: 27px;
  display: flex;
  align-items: center;
  user-select: none;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    height: initial;
    width: 100%;
    justify-content: flex-end;
    z-index: 1;
  }
`;

const MobileNavigation = styled.div`
  background: ${colors.outerSpace};
  position: fixed;
  z-index: 1000;
  inset: 0;
  min-height: 100vh;
  min-height: 100svh;
  min-height: 100dvh;
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: max(110px, env(safe-area-inset-bottom, 0px));
  
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    display: none;
  }
`;

const DesktopNavigation = styled.div`
  display: none;
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    display: flex;
  }
`;

const MobileHeaderActions = styled.div`
  display: none;

  @media screen and (max-width: calc(${breakpoints.smallLaptop} - 1px)) {
    display: flex;
    align-items: center;
    gap: 8px;
    position: fixed;
    right: 24px;
    z-index: 1001;
    ${mobileHeaderActionsPosition}
  }

  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    display: none;
  }
`;

const MenuIcons = styled.div<{ opened: boolean }>`
  @media screen and (max-width: calc(${breakpoints.smallLaptop} - 1px)) {
    background: ${({ opened }) => (opened ? 'initial' : '#fff')};
    border-radius: 8px;
    padding: 6px 8px;
    max-width: 40px;
    max-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    display: none;
  }
`;

type ExternalMenuLinkProps = {
  link: string;
  linkId?: string;
  isButton?: boolean;
  label?: string;
  navigationTheme: 'light' | 'dark';
  onNavigate: () => void;
};

const ExternalMenuLink: React.FC<ExternalMenuLinkProps> = ({
  link,
  linkId,
  isButton,
  label,
  navigationTheme,
  onNavigate,
}) => {
  const href = useOutboundHref(link) || link;
  return (
    <ExternalMainLink
      href={href}
      target='_blank'
      rel='noopener'
      id={linkId}
      data-cta-location={teamsCtaLocation(link, 'nav')}
      onClick={onNavigate}
    >
      <LinkLabel theme={navigationTheme} isButton={isButton}>
        <div>{label}</div>
      </LinkLabel>
    </ExternalMainLink>
  );
};

type ExternalSubmenuLinkProps = {
  link: string;
  linkId?: string;
  label?: string;
  isFirst: boolean;
  isLast: boolean;
  onNavigate: () => void;
};

const ExternalSubmenuLink: React.FC<ExternalSubmenuLinkProps> = ({
  link,
  linkId,
  label,
  isFirst,
  isLast,
  onNavigate,
}) => {
  const href = useOutboundHref(link) || link;
  return (
    <ExternalInnerLink
      href={href}
      target='_blank'
      rel='noopener'
      id={linkId}
      isFirst={isFirst}
      isLast={isLast}
      data-cta-location={teamsCtaLocation(link, 'nav')}
      onClick={onNavigate}
    >
      {label}
    </ExternalInnerLink>
  );
};
